// Minecarts on rails (RED-5/6, M16.2). PURE (ENG-1): no three/DOM/audio. A cart is
// tracked rail-relatively — the current rail cell, the edge it entered by, its
// heading, and a progress `t` in [0,1) along the cell's centreline path (a straight
// line for a straight rail, an L through the cell centre for a curve). Because the
// speed cap is < 0.5 blocks/tick, a cart crosses at most one landmark per tick, so
// `t` advances by `speed` and wraps into the next cell cleanly. The host adapts the
// world (rail shapes + powered-rail power) and renders from the derived (x,y,z).
import { MINECART } from '../config';
import { DIR_VEC, type Facing } from '../game/redstone/types';
import { RAIL_CONNECTIONS, exitConnection, raisedEnd, OPP_FACING, type RailShape } from '../game/rail';

/** Minecart variants (RED-6). A plain cart rides; the specials carry a payload:
 *  chest/hopper hold items (a hopper also vacuums drops), tnt explodes on a hard
 *  impact, furnace self-propels while fuelled. */
export type MinecartKind = 'minecart' | 'chest_minecart' | 'hopper_minecart' | 'tnt_minecart' | 'furnace_minecart';

/** The item stored in a container cart's slot (opaque to the physics). */
export interface CartSlot { item: number; count: number; }

export interface Minecart {
  readonly id: number;
  readonly kind: MinecartKind;
  /** The rail cell the cart is currently on. */
  cx: number; cy: number; cz: number;
  /** The edge the cart entered this cell by (its path runs entry → centre → dir). */
  entry: Facing;
  /** Heading — the edge the cart is leaving toward. */
  dir: Facing;
  /** Progress along the cell path, [0,1). 0 = entry edge, 0.5 = centre, 1 = exit. */
  t: number;
  /** Speed in blocks/tick (≥ 0). */
  speed: number;
  /** Derived world position (rail centreline), refreshed each tick for rendering. */
  x: number; y: number; z: number;
  /** Container payload (chest/hopper carts) — the host reads/writes slots. */
  items?: (CartSlot | null)[];
  /** Remaining self-propulsion ticks (furnace cart, topped up by fuel). */
  fuel?: number;
  /** Primed-TNT fuse ticks once a tnt cart is ignited; ≤0 = inert. */
  fuse?: number;
}

/** Slot counts per container cart (Java: 27 like a single chest). */
export const CART_INVENTORY_SIZE = 27;
/** Ticks of push one coal (or any furnace fuel) grants a furnace cart. */
export const FURNACE_FUEL_TICKS = 1200;
/** Carts closer than this (world units, same y) collide. */
export const CART_COLLIDE_DIST = 1;
/** A tnt cart travelling at least this fast detonates on impact. */
export const TNT_CART_IMPACT_SPEED = 0.15;
/** Fuse ticks a primed tnt cart burns before it explodes. */
export const TNT_CART_FUSE = 10;

/** What the physics reads from the world. */
export interface RailWorld {
  /** The rail shape at a cell, or null if there is no rail there. */
  railShapeAt(x: number, y: number, z: number): RailShape | null;
  /** For a powered rail: is it currently powered? null when the cell isn't a
   *  powered rail (a normal rail applies rolling friction instead). */
  poweredRailAt(x: number, y: number, z: number): boolean | null;
}

const EPS = 1e-6;

export class MinecartSystem {
  readonly carts: Minecart[] = [];
  private nextId = 1;

  get count(): number { return this.carts.length; }

  /** Spawn a cart at the centre of rail cell (cx,cy,cz), heading `dir`. Container
   *  carts (chest/hopper) start with an empty inventory. */
  spawn(cx: number, cy: number, cz: number, dir: Facing = 'north', speed = 0, kind: MinecartKind = 'minecart'): Minecart {
    const cart: Minecart = { id: this.nextId++, kind, cx, cy, cz, entry: OPP_FACING[dir], dir, t: 0.5, speed, x: 0, y: 0, z: 0 };
    if (kind === 'chest_minecart' || kind === 'hopper_minecart') cart.items = new Array<CartSlot | null>(CART_INVENTORY_SIZE).fill(null);
    if (kind === 'furnace_minecart') cart.fuel = 0;
    refreshPos(cart, null);
    this.carts.push(cart);
    return cart;
  }

  /** Fuel a furnace cart so it self-propels for a while (Java: push with coal). */
  fuelFurnace(cart: Minecart, ticks = FURNACE_FUEL_TICKS): void {
    cart.fuel = Math.max(0, cart.fuel ?? 0) + ticks;
  }

  remove(cart: Minecart): void {
    const i = this.carts.indexOf(cart);
    if (i >= 0) this.carts.splice(i, 1);
  }

  clear(): void { this.carts.length = 0; }

  /** Advance every cart one tick, then resolve cart↔cart collisions. */
  tick(world: RailWorld): void {
    for (const cart of this.carts) step(cart, world);
    resolveCartCollisions(this.carts);
  }
}

/**
 * Resolve cart↔cart contact (RED-6). Equal-mass carts on the same level within
 * CART_COLLIDE_DIST exchange momentum: two carts heading the same way swap speeds
 * (a fast rear cart shunts a slow/stopped front one forward, Newton's-cradle style,
 * and itself stops); carts meeting head-on both bump to a halt. Pure — mutates only
 * `speed`; the next tick re-derives positions.
 */
export function resolveCartCollisions(carts: readonly Minecart[]): void {
  for (let i = 0; i < carts.length; i++) {
    for (let j = i + 1; j < carts.length; j++) {
      const a = carts[i]!, b = carts[j]!;
      if (a.cy !== b.cy) continue;
      const dist = Math.hypot(b.x - a.x, b.z - a.z);
      if (dist === 0 || dist >= CART_COLLIDE_DIST) continue;
      // A hard crash primes a tnt cart's fuse (Java: fast impact detonates it).
      const closing = Math.max(a.speed, b.speed);
      if (closing >= TNT_CART_IMPACT_SPEED) {
        if (a.kind === 'tnt_minecart' && (a.fuse ?? 0) <= 0) a.fuse = TNT_CART_FUSE;
        if (b.kind === 'tnt_minecart' && (b.fuse ?? 0) <= 0) b.fuse = TNT_CART_FUSE;
      }
      if (a.dir === b.dir) {
        const s = a.speed; a.speed = b.speed; b.speed = s; // same heading → swap (momentum transfer)
      } else {
        a.speed = 0; b.speed = 0; // head-on / crossing → both stop
      }
    }
  }
}

function step(cart: Minecart, world: RailWorld): void {
  const shape = world.railShapeAt(cart.cx, cart.cy, cart.cz);
  if (!shape) { // derailed — coast to a stop in place
    cart.speed *= MINECART.DERAIL_DRAG;
    if (cart.speed < MINECART.STOP_EPS) cart.speed = 0;
    return;
  }
  realign(cart, shape);
  applyRailForce(cart, world, shape);
  advance(cart, world);
  refreshPos(cart, world.railShapeAt(cart.cx, cart.cy, cart.cz));
}

/** Keep the heading consistent with the rail under the cart (the shape may have
 *  changed, e.g. a neighbour rail was placed/broken). */
function realign(cart: Minecart, shape: RailShape): void {
  const exit = exitConnection(shape, cart.entry);
  if (exit) { cart.dir = exit; return; }
  // Entry side no longer connects — re-seat on this rail toward its first exit.
  const [a] = RAIL_CONNECTIONS[shape];
  cart.dir = a;
  cart.entry = OPP_FACING[a];
}

/** Powered-rail push / brake, or rolling friction on a normal rail; a fuelled
 *  furnace cart adds its own push on any rail. */
function applyRailForce(cart: Minecart, world: RailWorld, shape: RailShape): void {
  const powered = world.poweredRailAt(cart.cx, cart.cy, cart.cz);
  if (powered === true) {
    if (cart.speed <= MINECART.LAUNCH_MIN) launch(cart, world, shape);
    cart.speed += MINECART.POWERED_ACCEL;
  } else if (powered === false) {
    cart.speed *= MINECART.BRAKE;
    if (cart.speed < MINECART.STOP_EPS) cart.speed = 0;
  } else {
    cart.speed *= MINECART.DRAG;
    if (cart.speed < MINECART.STOP_EPS) cart.speed = 0;
  }
  // A fuelled furnace cart self-propels (Java: burns fuel to keep rolling).
  if (cart.kind === 'furnace_minecart' && (cart.fuel ?? 0) > 0) {
    if (cart.speed <= MINECART.LAUNCH_MIN) launch(cart, world, shape);
    cart.speed += MINECART.POWERED_ACCEL;
    cart.fuel = (cart.fuel ?? 0) - 1;
  }
  if (cart.speed > MINECART.MAX_SPEED) cart.speed = MINECART.MAX_SPEED;
}

/** A powered rail launching a resting cart picks the connection whose neighbour is
 *  open track (preferring the current heading), so a cart eases off a station. */
function launch(cart: Minecart, world: RailWorld, shape: RailShape): void {
  const [a, b] = RAIL_CONNECTIONS[shape];
  const open = (d: Facing): boolean => world.railShapeAt(cart.cx + DIR_VEC[d][0], cart.cy, cart.cz + DIR_VEC[d][2]) !== null;
  const pick = open(cart.dir) ? cart.dir : open(a) ? a : open(b) ? b : cart.dir;
  cart.dir = pick;
  cart.entry = OPP_FACING[pick];
  // Re-seat progress consistent with the chosen heading (still centred at rest).
  cart.t = 0.5;
}

/** Integrate progress; wrap into the next cell when the cart reaches the exit edge.
 *  Slopes shift the target cell's y: leaving an ascending rail by its raised end
 *  climbs +1; by its low end it steps down onto a same-level rail or the bottom of
 *  another slope. A flat rail may roll onto a slope one block up or down. */
function advance(cart: Minecart, world: RailWorld): void {
  cart.t += cart.speed;
  let guard = 0;
  while (cart.t >= 1 - EPS && cart.speed > 0 && guard++ < 4) {
    const shape = world.railShapeAt(cart.cx, cart.cy, cart.cz);
    const raised = shape ? raisedEnd(shape) : null;
    const nx = cart.cx + DIR_VEC[cart.dir][0];
    const nz = cart.cz + DIR_VEC[cart.dir][2];
    const candidates = raised === cart.dir ? [cart.cy + 1] // leaving the raised end → up
      : raised && OPP_FACING[raised] === cart.dir ? [cart.cy, cart.cy - 1] // low end → level or down
        : [cart.cy, cart.cy + 1, cart.cy - 1]; // flat rail → level, up onto a slope, or down
    const nentry = OPP_FACING[cart.dir];
    let moved = false;
    for (const ny of candidates) {
      const nshape = world.railShapeAt(nx, ny, nz);
      const nexit = nshape ? exitConnection(nshape, nentry) : null;
      if (nshape && nexit) {
        cart.cx = nx; cart.cy = ny; cart.cz = nz; cart.entry = nentry; cart.dir = nexit; cart.t -= 1;
        moved = true; break;
      }
    }
    if (!moved) { cart.t = 1 - EPS; cart.speed = 0; break; } // dead end / non-connecting
  }
  if (cart.t < 0) cart.t = 0;
}

/** Reconstruct the world position from (cell, entry, dir, t): the L-path
 *  entry-edge → centre → exit-edge (a straight line when entry/dir are opposite).
 *  On an ascending rail the y rises linearly from the low edge to the raised edge
 *  along the slope axis. */
function refreshPos(cart: Minecart, shape: RailShape | null): void {
  const cxc = cart.cx + 0.5, czc = cart.cz + 0.5;
  const entryV = DIR_VEC[cart.entry], dirV = DIR_VEC[cart.dir];
  const entryX = cxc + 0.5 * entryV[0], entryZ = czc + 0.5 * entryV[2];
  const exitX = cxc + 0.5 * dirV[0], exitZ = czc + 0.5 * dirV[2];
  if (cart.t < 0.5) {
    const u = cart.t / 0.5;
    cart.x = entryX + (cxc - entryX) * u;
    cart.z = entryZ + (czc - entryZ) * u;
  } else {
    const u = (cart.t - 0.5) / 0.5;
    cart.x = cxc + (exitX - cxc) * u;
    cart.z = czc + (exitZ - czc) * u;
  }
  cart.y = cart.cy + MINECART.RAIL_Y;
  const raised = shape ? raisedEnd(shape) : null;
  if (raised) {
    const onX = raised === 'east' || raised === 'west';
    const axisPos = onX ? cart.x - cart.cx : cart.z - cart.cz; // 0..1 within the cell
    const rise = raised === 'east' || raised === 'south' ? axisPos : 1 - axisPos;
    cart.y += Math.max(0, Math.min(1, rise));
  }
}
